import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import classNames from 'classnames';

import { PageType } from '../../../types/__generated__';
import { PageMetaContext } from '../../context';
import { useNotNullableContext } from '../../hooks';
import { Experiment } from '../ABTesting/Experiment';
import { Variation } from '../ABTesting/Variation';

import styles from './DefaultLayout.scss';
import { Experiments } from '../../constants/experiments';
import isMobile from '../../utils/isMobile';

type DefaultLayoutProps = {
  slots: {
    featureImage: ReactNode;
    ownerAvatar: ReactNode;
    titles: ReactNode;
    totals: ReactNode;
    callsToAction: ReactNode;
    titlesSupplementary?: ReactNode;
    causeMini: ReactNode;
    main: ReactNode;
    aside: ReactNode;
    teamAndCompany?: ReactNode;
    contentFooterPrimary: ReactNode;
    contentFooterSecondary: ReactNode;
  };
};

const DefaultLayout: FunctionComponent<DefaultLayoutProps> = ({
  slots: {
    titlesSupplementary,
    titles,
    ownerAvatar,
    main,
    contentFooterSecondary,
    contentFooterPrimary,
    causeMini,
    callsToAction,
    aside,
    teamAndCompany,
    featureImage,
    totals,
  },
}) => {
  // B2C_008 - remove/replace sticky bar on mobile
  // Used to ensure our experiment only runs for fundraising pages
  // Remove this when removing the experiment
  const { pageType } = useNotNullableContext(PageMetaContext);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    setIsMobileDevice(isMobile(window));
  }, []);

  return (
    <>
      <div data-qa="app" className={styles.container}>
        <div className={styles.grid}>
          <section className={styles.featureImage}>{featureImage}</section>

          <article className={styles.title}>
            <section className={styles.pageOwner} data-qa="page-owner">
              <div className={styles.pageOwnerAvatar}>{ownerAvatar}</div>
              <div className={styles.pageOwnerTitles}>{titles}</div>
            </section>
          </article>

          <div className={styles.totalsWrapper}>
            <section className={styles.totals}>{totals}</section>

            {/*B2C_008 - remove/replace sticky bar on mobile*/}
            {/*Forces control group if page is not Fundraising*/}
            {/*Replace this with the control group and remove data-qa when removing experiment*/}
            <Experiment
              slug={Experiments.b2c_hide_sticky_bar}
              force={
                pageType.toUpperCase() !== PageType.Fundraising
                  ? 'A'
                  : undefined
              }
              skip={!isMobileDevice}
            >
              <Variation name="A">
                <section
                  data-qa="B2C_008-variation-a"
                  className={styles.callsToAction}
                >
                  {callsToAction}
                </section>
              </Variation>
              <Variation name="B">
                <section
                  data-qa="B2C_008-variation-b"
                  className={classNames(
                    styles.callsToAction,
                    styles.callsToActionB2C_008,
                  )}
                >
                  {callsToAction}
                </section>
              </Variation>
            </Experiment>
          </div>

          {titlesSupplementary && (
            <section
              className={styles.additional}
              data-qa="title-supplementary-slot"
            >
              <div className={styles.additionalWrapper}>
                {titlesSupplementary}
              </div>
            </section>
          )}

          <section className={styles.causeMini}>{causeMini}</section>

          <main className={styles.main}>{main}</main>

          <aside
            className={classNames(
              styles.aside,
              'jg-bdt',
              'jg-bd--very-light-grey',
            )}
          >
            {aside}
          </aside>

          {teamAndCompany && (
            <section
              className={styles.teamAndCompany}
              data-qa="team-and-company-slot"
            >
              {teamAndCompany}
            </section>
          )}
        </div>
      </div>
      <div className={styles.bottomWrapper}>
        <div className={styles.bottom}>{contentFooterPrimary}</div>
      </div>
      <div className={styles.bottomWrapper}>
        <div className={styles.bottom}>{contentFooterSecondary}</div>
      </div>
    </>
  );
};

export default DefaultLayout;
